/**
 * API клиент для уроков
 * apps/web/src/lib/lessons-api.ts
 */

const API_BASE = '/api';

// Типы
export interface Lesson {
  id: string;
  collectionId: string;
  moduleId: string;
  title: string;
  type: 'text' | 'audio' | 'video';
  content: {
    text: string;
    format: 'markdown' | 'html';
    keyPoints?: string[];
    examples?: Array<{
      title: string;
      code: string;
      language: string;
    }>;
  };
  media?: {
    audioUrl?: string;
    videoUrl?: string;
    thumbnailUrl?: string;
    duration?: number;
  };
  settings: {
    allowSimplify: boolean;
    allowExpand: boolean;
    showKeyPoints: boolean;
  };
  aiGenerated: boolean;
  stats: {
    qualityScore: number;
    viewsCount: number;
    avgTimeSpent: number;
    feedbackPositive: number;
    feedbackNegative: number;
  };
  meta: {
    version: number;
    createdAt: string;
    updatedAt: string;
  };
}

export interface LessonUserProgress {
  lessonId: string;
  userId: string;
  viewed: boolean;
  viewedAt?: number;
  timeSpent: number;
  feedback?: 'positive' | 'negative';
}

export interface LessonResponse {
  lesson: Lesson;
  userProgress?: LessonUserProgress;
}

export interface LessonAdaptResponse {
  content: Lesson['content'];
  mode: 'simplify' | 'expand';
}

// Получить заголовки авторизации
import { getAuthHeaders } from "./auth-headers";

/**
 * Получить урок по ID
 */
export async function getLesson(lessonId: string): Promise<LessonResponse> {
  const response = await fetch(`${API_BASE}/lessons/${lessonId}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to load lesson');
  }

  return response.json();
}

/**
 * Отметить урок как просмотренный
 */
export async function markLessonViewed(
  lessonId: string,
  timeSpent: number
): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/lessons/${lessonId}/view`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ timeSpent }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to mark lesson as viewed');
  }

  return response.json();
}

/**
 * Отправить feedback по уроку
 */
export async function submitLessonFeedback(
  lessonId: string,
  type: 'positive' | 'negative' | 'error',
  comment?: string,
  errorText?: string
): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/lessons/${lessonId}/feedback`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ type, comment, errorText }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to submit feedback');
  }

  return response.json();
}

/**
 * Запросить адаптацию урока (упростить/расширить)
 */
export async function adaptLesson(
  lessonId: string,
  mode: 'simplify' | 'expand'
): Promise<LessonAdaptResponse | { status: 'generation_required' }> {
  const response = await fetch(`${API_BASE}/lessons/${lessonId}/adapt`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ mode }),
  });

  // 202 = нужна AI генерация
  if (response.status === 202) {
    return { status: 'generation_required' };
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to adapt lesson');
  }

  return response.json();
}

/**
 * Отметить урок как просмотренный в коллекции (для разблокировки теста)
 */
export async function markCollectionLessonViewed(
  collectionId: string,
  moduleId: string
): Promise<{ success: boolean; xpEarned: number }> {
  const response = await fetch(
    `${API_BASE}/collections/${collectionId}/modules/${moduleId}/lesson-viewed`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to mark lesson as viewed');
  }

  return response.json();
}